import { omit } from 'ramda';
import { Alert, Box, InputLabel, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { DeviceCardProps } from '../DeviceCardProps';
import DriveUnitPortBoard from './DriveUnitPortBoard';
import { useConfigurePanelPortChange } from '../../../../hooks/controls/useConfigurePanelPortChange';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../../../redux/ApplicationSlice';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN, isUnsupportedDriveUnitPortRawValue } from '../../../../utils/driveUnitPortSelection';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';

export interface DriveUnitPortControlLabels {
  labelId: string;
  label: React.ReactNode;
  selectId: string;
  selectLabelId: string;
  labelPopover?: React.ReactNode;
  labelPopoverId?: string;
}

interface DriveUnitPortControlProps extends DeviceCardProps, DriveUnitPortControlLabels {
  /**
   * When `setPortControlValue` is set, port state and editor sync are owned by the parent (e.g. hoisted above Collapse).
   * Omit to use internal `useControlInitialization`.
   */
  portControlValue?: number;
  setPortControlValue?: (value: number) => void;
  /** When true, port changes update local/parent state only (no Monaco `updateParameter`). */
  skipEditorParameterSync?: boolean;
  portConflictPresentation?: 'dialog' | 'inline';
  unsupportedRawValue?: string;
}

type DriveUnitPortControlViewProps = DeviceCardProps &
  DriveUnitPortControlLabels & {
    port: number | undefined;
    onPortChange: (portNumber: number) => void;
    portConflictPresentation?: 'dialog' | 'inline';
    unsupportedRawValue?: string;
    portWriteBlocked?: boolean;
  };

/** Explains a port click that was rejected because the device declaration could not be updated in code. */
const PortWriteBlockedStatus = ({ blocked }: { blocked?: boolean }) => {
  const { t } = useTranslation();
  if (!blocked) return null;

  return (
    <Alert severity="warning">
      {t('controlPanel.devices.portWriteBlocked.description', {
        defaultValue:
          'The port was not changed because this device could not be updated in your program code. Check the device declaration, then try again.',
      })}
    </Alert>
  );
};

/** Presentational port grid; no hooks (safe to render inside Collapse). */
const DriveUnitPortControlView = (props: DriveUnitPortControlViewProps) => {
  const { port, onPortChange, labelId, label, labelPopover, labelPopoverId } = props;
  const electronicKey = `${props.id}:${props.parameterName}`;
  const selectedSlot = typeof port === 'number' && Number.isFinite(port) ? port : Number.NaN;
  const conflictPresentation = props.portConflictPresentation ?? 'dialog';

  return (
    <Box>
      <InputLabel sx={{ paddingBottom: 1 }} id={labelId}>
        {labelPopover && labelPopoverId ? (
          <Stack direction="row" spacing={1}>
            <Typography>{label}</Typography>
            <HelpPopover id={labelPopoverId} text={labelPopover} />
          </Stack>
        ) : (
          label
        )}
      </InputLabel>
      <Stack direction="column" spacing={1}>
        <UnsupportedCodeStatus
          code={props.unsupportedRawValue}
          descriptionI18nKey="controlPanel.devices.unsupportedPortArgument.description"
          descriptionDefaultValue="Unsupported port argument: {{code}}"
        />
        <PortWriteBlockedStatus blocked={props.portWriteBlocked} />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DriveUnitPortBoard id={electronicKey} selected={selectedSlot} onPortClick={onPortChange} conflictPresentation={conflictPresentation} />
        </Box>
      </Stack>
    </Box>
  );
};

/** Port grid with internal `useControlInitialization` (legacy path for cards that mount this inside Collapse). */
const DriveUnitPortControlInternal = (props: DriveUnitPortControlProps) => {
  const {
    value: port,
    setValue: setPort,
    unsupportedRawValue,
  } = useControlInitialization<number>(props, {
    parseValue: (v) => Number.parseInt(v, 10),
    isUnsupportedRawValue: isUnsupportedDriveUnitPortRawValue,
  });
  const dispatch = useDispatch();
  const electronicKey = `${props.id}:${props.parameterName}`;
  const registryRev = props.driveUnitPortRegistryRev ?? 0;
  const effectivePort = unsupportedRawValue ? undefined : port;

  useEffect(() => {
    if (
      typeof effectivePort !== 'number' ||
      !Number.isFinite(effectivePort) ||
      effectivePort < DRIVE_UNIT_PORT_MIN ||
      effectivePort > DRIVE_UNIT_PORT_MAX
    ) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: electronicKey }));
      return;
    }
    dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: effectivePort, electronic: electronicKey }));
  }, [dispatch, effectivePort, electronicKey, registryRev]);

  const { commitPortChange, portWriteBlocked } = useConfigurePanelPortChange(props, port, setPort);
  const cardFields = omit(['portControlValue', 'setPortControlValue', 'skipEditorParameterSync'], props) as Omit<
    DriveUnitPortControlViewProps,
    'port' | 'onPortChange'
  >;
  return (
    <DriveUnitPortControlView
      {...cardFields}
      port={effectivePort}
      onPortChange={commitPortChange}
      portWriteBlocked={portWriteBlocked}
      labelId={props.labelId}
      label={props.label}
      selectId={props.selectId}
      selectLabelId={props.selectLabelId}
      labelPopover={props.labelPopover}
      labelPopoverId={props.labelPopoverId}
      unsupportedRawValue={unsupportedRawValue}
    />
  );
};

const DriveUnitPortControlHoisted = (
  props: DriveUnitPortControlProps & {
    portControlValue: number | undefined;
    setPortControlValue: (value: number) => void;
  }
) => {
  const { commitPortChange, portWriteBlocked } = useConfigurePanelPortChange(props, props.portControlValue, props.setPortControlValue);
  const cardFields = omit(['portControlValue', 'setPortControlValue', 'skipEditorParameterSync'], props) as Omit<
    DriveUnitPortControlViewProps,
    'port' | 'onPortChange'
  >;
  return (
    <DriveUnitPortControlView
      {...cardFields}
      port={props.portControlValue}
      onPortChange={commitPortChange}
      portWriteBlocked={portWriteBlocked}
      labelId={props.labelId}
      label={props.label}
      selectId={props.selectId}
      selectLabelId={props.selectLabelId}
      labelPopover={props.labelPopover}
      labelPopoverId={props.labelPopoverId}
      unsupportedRawValue={props.unsupportedRawValue}
    />
  );
};

const DriveUnitPortControl = (props: DriveUnitPortControlProps) => {
  if (props.setPortControlValue !== undefined) {
    return <DriveUnitPortControlHoisted {...props} portControlValue={props.portControlValue} setPortControlValue={props.setPortControlValue} />;
  }

  return <DriveUnitPortControlInternal {...props} />;
};

export default DriveUnitPortControl;
